import React, { useContext, useEffect, useState } from 'react'
import { StoresContext } from '@App/contexts'
import Navigation from '@App/navigation'
import { useNavigate, useParams } from 'react-router'
import { PARAM } from '@App/navigation/param'
import { downloadCatalogTransferDocument } from '@App/api/routes/rightsholder/catalog-transfer'
import { openExternalHttp } from '@App/navigation/safe-navigation'
import ModalPortal from '@Components/Popups/ModalPortal'
import ContentHeader from '@Components/ContentHeader'
import StatusHeader from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferView/StatusHeader'
import ReviewActions from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferView/ReviewActions'
import ReviewDecisionPopup from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferView/ReviewDecisionPopup'
import ReviewDetails from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferView/ReviewDetails'
import CatalogTransferStatusPill from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferStatusPill'
import { CatalogTransferReviewDecisionStatus } from '@Entities/rightsholder/catalog-transfer-view'
import {
    getCatalogTransferDecisionForMember,
    getCatalogTransferDecisionLabel,
    isCatalogTransferDecisionFinal,
} from '@App/helpers/get-catalog-transfer-decision'
import './index.scss'
import { observer } from 'mobx-react'

const CatalogTransferView = () => {
    const { catalogTransferWizardStore, rightsholderStore, preloaderStore } = useContext(StoresContext)
    const params = useParams<typeof PARAM>()
    const navigate = useNavigate()
    const [selectedDecision, setSelectedDecision] = useState<CatalogTransferReviewDecisionStatus | null>(null)

    const currentRightsholderId = rightsholderStore.currentRightsholderId
    const currentTransfer = catalogTransferWizardStore.currentCatalogTransfer

    useEffect(() => {
        preloaderStore.openTransparentPreloader(true)
        const transferId = Number(params.transferId)
        catalogTransferWizardStore
            .setCurrentCatalogTransfer(transferId)
            .finally(() => preloaderStore.openTransparentPreloader(false))

        return () => {
            catalogTransferWizardStore.clearCurrentCatalogTransfer()
        }
    }, [params.transferId])

    const downloadLastestWorks = async () => {
        preloaderStore.openTransparentPreloader(true)
        const fileToDownload = currentTransfer?.uploadedDocs
            .filter((doc) => doc.uploadType === 'CATALOG_IMPORT')
            .sort((a, b) => a.id - b.id)
            .pop()
        if (!fileToDownload) {
            preloaderStore.openTransparentPreloader(false)
            return
        }
        await onDownloadFile(fileToDownload.id)
    }

    const onDownloadFile = async (catalogTransferMemberUploadId: number) => {
        preloaderStore.openTransparentPreloader(true)
        try {
            const response = await downloadCatalogTransferDocument(
                currentRightsholderId,
                currentTransfer!.id,
                catalogTransferMemberUploadId
            )
            openExternalHttp(response.data.downloadLink)
        } finally {
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const closeDecisionModal = () => {
        setSelectedDecision(null)
    }

    const decisionForRightsholder = getCatalogTransferDecisionForMember(currentTransfer, currentRightsholderId)
    const isReviewable =
        !isCatalogTransferDecisionFinal(decisionForRightsholder) &&
        Number(currentTransfer?.submittingMember.ipId) !== currentRightsholderId &&
        currentTransfer?.catalogTransferStatus === 'SUBMITTED'

    const decisionSummaryLabel = getCatalogTransferDecisionLabel(decisionForRightsholder)

    const handleDecisionSubmit = async (notes: string) => {
        if (!selectedDecision) {
            return
        }
        try {
            const didSubmit = await catalogTransferWizardStore.submitReviewDecision(selectedDecision, notes)
            if (didSubmit) {
                navigate(`${Navigation.rightholder.catalogTransfer.root(currentRightsholderId)}#for-my-review`)
            }
        } finally {
            closeDecisionModal()
        }
    }

    return (
        <div className="catalog-transfer-wrapper">
            {currentTransfer && (
                <>
                    <ContentHeader title="Catalog Transfer Tool">
                        <div className="ct-status-pill">
                            <span className="ct-status-pill-label">Status:</span>
                            <CatalogTransferStatusPill transfer={currentTransfer} canReviewActions={isReviewable} />
                        </div>
                    </ContentHeader>
                    <StatusHeader currentTransfer={currentTransfer} currentRightsholderId={currentRightsholderId} />
                    <div className="ct-review-step ct-view">
                        {isCatalogTransferDecisionFinal(decisionForRightsholder) && (
                            <div className="ct-decision-summary">
                                <p className="ct-decision-summary-title">Your Decision on this transfer:</p>
                                <p className="ct-decision-summary-value">{decisionSummaryLabel}</p>
                            </div>
                        )}
                        {isReviewable && (
                            <ReviewActions
                                onApprove={() => setSelectedDecision('APPROVE')}
                                onDeny={() => setSelectedDecision('DENY')}
                            />
                        )}
                        <ReviewDetails
                            currentTransfer={currentTransfer}
                            onDownloadWorkReport={downloadLastestWorks}
                            onDownloadFile={onDownloadFile}
                        />
                    </div>
                    <ModalPortal isOpen={selectedDecision != null} onClose={closeDecisionModal}>
                        <ReviewDecisionPopup
                            decision={selectedDecision}
                            onSubmit={handleDecisionSubmit}
                            onClose={closeDecisionModal}
                        />
                    </ModalPortal>
                </>
            )}
        </div>
    )
}
export default observer(CatalogTransferView)
